import { AsyncStateSelector } from '@lib/selectors';
import { HttpStatusCode } from '@sonar-software/sonar-http';
import { IconType } from 'antd/es/notification/interface';

import { Alert, AlertProps } from 'antd';
import { AsyncStateStatus, isErrored, isFailed, isReceived, isRequested } from '@sonar-software/xuder';
import React, { useMemo, useState } from 'react';
import { StatusCodeHandlers, useAsyncProcessing } from '@hooks';

function getAlertOptions(options: AlertProps | string | undefined): AlertProps | undefined {
	if (typeof options === 'string') {
		return {
			message: options
		};
	}

	return options;
}

function getNotificationType(status: HttpStatusCode | AsyncStateStatus): IconType {
	const stateStatus = status as AsyncStateStatus;

	switch (true) {
	case isRequested(stateStatus):
		return 'info';
	case isErrored(stateStatus):
	case isFailed(stateStatus):
		return 'error';
	case isReceived(stateStatus):
		return 'success';
	default:
		return 'error';
	}
}

export interface StatusCodeAlertProps {
	options: AlertProps;
	statusCode: HttpStatusCode;
}

export interface AsyncStateAlertProps {
	byStatusCode?: StatusCodeAlertProps[];
	errored?: AlertProps | string;
	failed?: AlertProps | string;
	received?: AlertProps | string;
	requested?: AlertProps | string;
	stateSelector: AsyncStateSelector<unknown>;
}

export const AsyncStateAlert: React.FC<AsyncStateAlertProps> = ({
	byStatusCode,
	errored,
	failed,
	received,
	requested,
	stateSelector
}) => {
	const [ alert, setAlert ] = useState<React.ReactElement | null>(null);

	const statusCodeHandlers: StatusCodeHandlers<AlertProps> | undefined = useMemo(
		() => byStatusCode?.reduce(
			(codes, entry) => {
				codes[ entry.statusCode ] = entry.options;

				return codes;
			},
		{} as StatusCodeHandlers<AlertProps>
		),
		[ byStatusCode ]
	);

	useAsyncProcessing<AlertProps>({
		allowMultipleHandlers: false,
		onStatus: (status, options) => {
			if (options) {
				setAlert(
					<Alert
						type={ getNotificationType(status) }
						{ ...getAlertOptions(options ?? '') } />
				);
			}
		},
		onStatusCode: (_, options) => {
			if (options) {
				setAlert(<Alert { ...options } />);
			}
		},
		stateSelector,
		statusCodeHandlers,
		statusHandlers: {
			errored: getAlertOptions(errored),
			failed: getAlertOptions(failed),
			received: getAlertOptions(received),
			requested: getAlertOptions(requested)
		},
		watchAllCalls: true
	});

	return alert;
};
